/**
 * Opt-in multi-domain locale routing.
 *
 * Parses NEXT_PUBLIC_LOCALE_DOMAINS ("en=https://en.example.com,fr=https://fr.example.com")
 * into a locale → origin map. When the variable is unset or empty, every helper
 * falls back to the single-domain behavior (NEXT_PUBLIC_PUBLIC_URL + path prefixes).
 *
 * NEXT_PUBLIC_INTERNAL_LOCALE_ORIGINS (same format, a locale may repeat) lists
 * origins that WordPress puts into links for a locale but this deployment does
 * not serve — e.g. the live EN domain in links from the live API on staging.
 *
 * Pure string/URL logic only — safe in edge middleware, server and client components.
 */

// only bare origins are allowed — a path/query/hash would break both the
// host matching and the path concatenation in buildAbsoluteInternalLink
const isValidOrigin = (value: string) => {
  try {
    return new URL(value).origin === value;
    // eslint-disable-next-line unused-imports/no-unused-vars
  } catch (e) {
    return false;
  }
};

/**
 * Parses "locale=origin" pairs; origins are normalized (no trailing slash),
 * invalid entries are ignored.
 */
const parseLocaleOriginPairs = (rawMapping: string) => {
  const pairs: { locale: string; origin: string }[] = [];

  for (const entry of rawMapping.split(",")) {
    const separatorIndex = entry.indexOf("=");
    if (separatorIndex === -1) continue;

    const locale = entry.slice(0, separatorIndex).trim().toLowerCase();
    const origin = entry
      .slice(separatorIndex + 1)
      .trim()
      .replace(/\/+$/, "");

    if (!locale || !isValidOrigin(origin)) continue;
    pairs.push({ locale, origin });
  }

  return pairs;
};

let cachedLocaleDomains: Record<string, string> | null = null;

/** Parsed "locale=origin" map from NEXT_PUBLIC_LOCALE_DOMAINS. */
export const getLocaleDomains = (): Record<string, string> => {
  if (cachedLocaleDomains) return cachedLocaleDomains;

  cachedLocaleDomains = Object.fromEntries(
    parseLocaleOriginPairs(process.env.NEXT_PUBLIC_LOCALE_DOMAINS ?? "").map(
      ({ locale, origin }) => [locale, origin],
    ),
  );
  return cachedLocaleDomains;
};

let cachedInternalLocaleHosts: { locale: string; host: string }[] | null = null;

/**
 * Locale of an absolute url whose host is listed in
 * NEXT_PUBLIC_INTERNAL_LOCALE_ORIGINS; undefined when none matches.
 */
export const getLocaleForInternalLocaleOrigin = (url: string) => {
  cachedInternalLocaleHosts ??= parseLocaleOriginPairs(
    process.env.NEXT_PUBLIC_INTERNAL_LOCALE_ORIGINS ?? "",
  ).map(({ locale, origin }) => ({
    locale,
    host: new URL(origin).host.toLowerCase(),
  }));

  let host: string;
  try {
    host = new URL(url).host.toLowerCase();
    // eslint-disable-next-line unused-imports/no-unused-vars
  } catch (e) {
    return undefined;
  }

  return cachedInternalLocaleHosts.find((entry) => entry.host === host)?.locale;
};

/** True when NEXT_PUBLIC_LOCALE_DOMAINS contains at least one valid mapping. */
export const isMultiDomainEnabled = () =>
  Object.keys(getLocaleDomains()).length > 0;

/** True when this locale is served from its own domain. */
export const isLocaleDomainMapped = (locale: string) =>
  Boolean(getLocaleDomains()[locale?.toLowerCase?.()]);

/**
 * Origin for the given locale — the mapped domain when configured,
 * otherwise NEXT_PUBLIC_PUBLIC_URL (trailing slash stripped).
 */
export const getDomainForLocale = (locale?: string) => {
  const mappedOrigin = locale
    ? getLocaleDomains()[locale.toLowerCase()]
    : undefined;
  return (
    mappedOrigin ??
    (process.env.NEXT_PUBLIC_PUBLIC_URL ?? "").replace(/\/+$/, "")
  );
};

/**
 * Locale whose mapped origin host (hostname incl. port, case-insensitive)
 * matches the given Host header value; undefined when no mapping matches.
 */
export const getLocaleForHost = (host: string | null | undefined) => {
  if (!host) return undefined;
  // x-forwarded-host may contain a comma-separated proxy chain — the first
  // entry is the originally requested host
  const normalizedHost = host.split(",")[0].trim().toLowerCase();

  for (const [locale, origin] of Object.entries(getLocaleDomains())) {
    if (new URL(origin).host.toLowerCase() === normalizedHost) {
      return locale;
    }
  }
  return undefined;
};

/** Strips a leading /{locale} segment: "/en/foo" → "/foo", "/en" → "/". */
export const stripLocalePrefix = (pathname: string, locale: string) => {
  const prefix = `/${locale}`;
  if (pathname === prefix) return "/";
  if (pathname.startsWith(`${prefix}/`)) return pathname.slice(prefix.length);
  return pathname;
};
